import React from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import { ResponseError, formatOTPWait, otpRetryAt, otpWaitSeconds } from "@norma-bi/bi-api";
import { useUnauthenticatedAPI } from "@/api";
import { useAccessToken } from "@/Auth/useAccessToken";
import { useAccountNumberInput } from "@/hooks/useAccountNumberInput";
import { useEmailInput } from "@/hooks/useEmailInput";
import { CHAT_PATH } from "@/constants";
import { CodeInput } from "./CodeInput";
import { LoginShowcase } from "./LoginShowcase";

type Feedback = { kind: "error"; text: string };

// The business reference and email of the last sign-in, when the user asked us to remember them.
const REMEMBERED_LOGIN_KEY = "foodics-bi.remembered-login";
function readRememberedLogin(): { business_ref?: string; email?: string } {
  try {
    return JSON.parse(localStorage.getItem(REMEMBERED_LOGIN_KEY) ?? "{}");
  } catch {
    return {};
  }
}

const linkButton =
  "text-[13px] text-(--s-muted-fg) underline underline-offset-3 hover:text-(--s-fg) disabled:no-underline disabled:hover:text-(--s-muted-fg)";
const primaryButton =
  "inline-flex h-9 w-full items-center justify-center gap-2 rounded-[10px] bg-(--s-primary) text-sm text-(--s-primary-fg) transition-opacity hover:opacity-85 active:translate-y-px disabled:opacity-50 focus-visible:shadow-[0_0_0_3px_var(--s-ring)] focus-visible:outline-hidden";
const outlineButton =
  "inline-flex h-9 items-center justify-center rounded-[10px] border border-(--s-input) text-sm text-(--s-fg) hover:bg-(--s-muted)";

export function Login() {
  const api = useUnauthenticatedAPI();
  const { setAccessToken } = useAccessToken();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const [remembered] = React.useState(readRememberedLogin);
  const { accountNumber, handleAccountNumberChange, isAccountNumberValid } = useAccountNumberInput(
    params.get("business_ref") ?? remembered.business_ref ?? "",
  );
  const { email, handleEmailChange, isEmailValid } = useEmailInput(
    params.get("email") ?? remembered.email ?? "",
  );
  const [remember, setRemember] = React.useState(true);
  const [showReferenceHelp, setShowReferenceHelp] = React.useState(false);
  const [challengeId, setChallengeId] = React.useState<string>();
  const [code, setCode] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [feedback, setFeedback] = React.useState<Feedback>();
  const [requestRetryAt, setRequestRetryAt] = React.useState<Date>();
  const [verifyRetryAt, setVerifyRetryAt] = React.useState<Date>();
  const [now, setNow] = React.useState(Date.now());
  React.useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  const requestWait = otpWaitSeconds(requestRetryAt, now);
  const verifyWait = otpWaitSeconds(verifyRetryAt, now);
  const isDemoReference = Number(accountNumber) < 0;

  async function enterDemo() {
    setBusy(true);
    setFeedback(undefined);
    try {
      const result = await api.postLoginDemo({
        DemoLoginRequest: { business_ref: Number(accountNumber), email },
      });
      setAccessToken(result.token.token);
      navigate(location.state?.redirectAfterLogin ?? CHAT_PATH, { replace: true });
    } catch (error) {
      setFeedback({
        kind: "error",
        text:
          error instanceof ResponseError && error.response.status === 404
            ? "This demo reference and email do not match. Check both and try again."
            : "The demo is unavailable right now. Please try again shortly.",
      });
    } finally {
      setBusy(false);
    }
  }

  async function requestCode() {
    setBusy(true);
    setFeedback(undefined);
    try {
      const result = await api.postLoginOtpChallenge({
        RequestOTPChallengeRequest: { business_ref: Number(accountNumber), email },
      });
      if (result.status === "code_sent" && result.challenge_id) {
        try {
          if (remember) {
            localStorage.setItem(
              REMEMBERED_LOGIN_KEY,
              JSON.stringify({ business_ref: accountNumber, email }),
            );
          } else localStorage.removeItem(REMEMBERED_LOGIN_KEY);
        } catch {
          // Storage can be blocked; signing in still works without it.
        }
        setChallengeId(result.challenge_id);
        setCode("");
        setRequestRetryAt(result.retry_at);
        setVerifyRetryAt(undefined);
        setFeedback(undefined);
      } else if (result.status === "business_not_found") {
        setFeedback({ kind: "error", text: "We could not find this Foodics business reference." });
      } else if (result.status === "user_not_found") {
        setFeedback({
          kind: "error",
          text: "We could not find this email in that Foodics business. Ask a Foodics admin to add it in the console, then try again.",
        });
      } else {
        setFeedback({
          kind: "error",
          text: "A code could not be sent right now. Please try again shortly.",
        });
      }
    } catch (error) {
      const retryAt = await otpRetryAt(error);
      if (retryAt) setRequestRetryAt(retryAt);
      setFeedback({
        kind: "error",
        text:
          error instanceof ResponseError && error.response.status === 429
            ? "Please wait until the countdown ends before requesting another code."
            : "A code could not be sent right now. Please try again shortly.",
      });
    } finally {
      setBusy(false);
    }
  }

  async function verifyCode(value = code) {
    if (!challengeId) return;
    setBusy(true);
    setFeedback(undefined);
    try {
      const result = await api.postLoginOtpVerify({
        VerifyOTPChallengeRequest: { challenge_id: challengeId, code: value },
      });
      if (result.status === "logged_in" && result.token) {
        setAccessToken(result.token.token);
        navigate(location.state?.redirectAfterLogin ?? CHAT_PATH, { replace: true });
        return;
      } else if (result.status === "access_changed") {
        setChallengeId(undefined);
        setFeedback({
          kind: "error",
          text: "Your Foodics access changed. Request a new code to continue.",
        });
      } else {
        setFeedback({ kind: "error", text: "This code can no longer be used. Request a new one." });
      }
    } catch (error) {
      const retryAt = await otpRetryAt(error);
      if (retryAt) setVerifyRetryAt(retryAt);
      setCode("");
      setFeedback({
        kind: "error",
        text:
          error instanceof ResponseError && error.response.status === 429
            ? "Too many incorrect attempts. Wait for the countdown or request a new code."
            : "The code is incorrect or has expired. Please try again.",
      });
    }
    setBusy(false);
  }

  return (
    <div className="s-login grid min-h-screen bg-white text-(--s-fg) lg:grid-cols-[minmax(0,1fr)_minmax(0,1.25fr)]">
      <div className="flex items-center justify-center px-6 py-10">
        <form
          className="flex w-full max-w-[360px] flex-col gap-5"
          onSubmit={(event) => {
            event.preventDefault();
            void (isDemoReference ? enterDemo() : challengeId ? verifyCode() : requestCode());
          }}
        >
          {challengeId ? (
            <button
              type="button"
              className="-ms-2 inline-flex h-8 items-center gap-1.5 self-start rounded-[10px] px-2 text-sm text-(--s-muted-fg) hover:bg-(--s-muted) hover:text-(--s-fg) focus-visible:shadow-[0_0_0_3px_var(--s-ring)] focus-visible:outline-hidden disabled:opacity-50"
              disabled={busy}
              onClick={() => {
                setChallengeId(undefined);
                setCode("");
                setFeedback(undefined);
                setRequestRetryAt(undefined);
                setVerifyRetryAt(undefined);
              }}
            >
              <svg
                viewBox="0 0 16 16"
                className="size-4"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M10 3L5 8l5 5" />
              </svg>
              Back to business and email
            </button>
          ) : null}
          <div className="flex items-center gap-2 text-sm font-medium">
            <span className="grid size-7 place-items-center rounded-lg bg-(--s-primary)">
              <svg viewBox="0 0 16 16" className="size-4 fill-(--s-primary-fg)" aria-hidden="true">
                <rect x="2" y="7" width="3" height="7" rx="1" />
                <rect x="6.5" y="2" width="3" height="12" rx="1" />
                <rect x="11" y="5" width="3" height="9" rx="1" />
              </svg>
            </span>
            Foodics BI
          </div>
          <div className="flex flex-col gap-1.5">
            {isDemoReference ? null : (
              <p className="text-xs text-(--s-muted-fg)">Step {challengeId ? 2 : 1} of 2</p>
            )}
            <h1 className="text-[22px] leading-tight font-medium tracking-tight text-balance">
              {challengeId ? "Check your email" : "Sign in to Foodics BI"}
            </h1>
            <p className="text-sm text-(--s-muted-fg)">
              {challengeId ? (
                <>
                  We sent a six-digit code to <b className="font-medium text-(--s-fg)">{email}</b>.
                </>
              ) : isDemoReference ? (
                "Enter the demo business reference and email to open the demo. No code is needed."
              ) : (
                "Enter your business reference and email. We'll email you a six-digit code. No password needed."
              )}
            </p>
          </div>

          {challengeId ? (
            <>
              <CodeInput
                value={code}
                onChange={(value) => {
                  setCode(value);
                  setFeedback(undefined);
                }}
                onComplete={(value) => {
                  if (verifyWait === 0) void verifyCode(value);
                }}
                invalid={feedback?.kind === "error"}
                disabled={busy}
              />
              {feedback ? (
                <p role="alert" className="text-[13px] text-(--s-destructive)">
                  {feedback.text}
                </p>
              ) : null}
              <button
                type="submit"
                className={primaryButton}
                disabled={busy || code.length !== 6 || verifyWait > 0}
              >
                {busy ? (
                  <>
                    <span
                      className="size-3.5 animate-spin rounded-full border-2 border-current border-t-transparent"
                      aria-hidden="true"
                    />
                    Signing you in…
                  </>
                ) : verifyWait > 0 ? (
                  `Verify code in ${formatOTPWait(verifyWait)}`
                ) : (
                  "Verify code"
                )}
              </button>
              <div className="grid grid-cols-2 gap-2">
                <a
                  className={outlineButton}
                  href="https://mail.google.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open Gmail
                </a>
                <a
                  className={outlineButton}
                  href="https://outlook.office.com/mail/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open Outlook
                </a>
              </div>
              <button
                type="button"
                className={`${linkButton} self-start tabular-nums`}
                disabled={busy || requestWait > 0}
                onClick={() => {
                  void requestCode();
                }}
              >
                {requestWait > 0 ? `Resend code in ${formatOTPWait(requestWait)}` : "Resend code"}
              </button>
            </>
          ) : (
            <>
              <div className="flex flex-col gap-1.5">
                <div className="flex items-baseline justify-between gap-2">
                  <label htmlFor="business-ref" className="text-[13px] font-medium">
                    Business reference
                  </label>
                  <button
                    type="button"
                    className={linkButton}
                    aria-expanded={showReferenceHelp}
                    aria-controls="business-ref-help"
                    onClick={() => setShowReferenceHelp((value) => !value)}
                  >
                    Where do I find this?
                  </button>
                </div>
                <input
                  id="business-ref"
                  className="s-login-input"
                  inputMode="text"
                  maxLength={7}
                  placeholder="643088"
                  value={accountNumber}
                  onChange={handleAccountNumberChange}
                  required
                />
                {showReferenceHelp ? (
                  <p
                    id="business-ref-help"
                    className="rounded-[10px] bg-(--s-muted) px-3 py-2.5 text-[13px] text-(--s-muted-fg)"
                  >
                    It's your Foodics business reference, also called the F5 number. You'll find it
                    in the Foodics Console.
                  </p>
                ) : null}
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="login-email" className="text-[13px] font-medium">
                  Email
                </label>
                <input
                  id="login-email"
                  className="s-login-input"
                  type="email"
                  autoComplete="email"
                  placeholder="name@restaurant.com"
                  value={email}
                  onChange={handleEmailChange}
                  required
                />
              </div>
              {isDemoReference ? null : (
                <label className="flex items-center gap-2 text-sm text-(--s-muted-fg)">
                  <input
                    type="checkbox"
                    className="size-4 accent-(--s-primary)"
                    checked={remember}
                    onChange={(event) => setRemember(event.target.checked)}
                  />
                  Remember this business on this device
                </label>
              )}
              {feedback ? (
                <p role="alert" className="text-[13px] text-(--s-destructive)">
                  {feedback.text}
                </p>
              ) : null}
              <button
                type="submit"
                className={primaryButton}
                disabled={
                  busy ||
                  !isAccountNumberValid ||
                  !isEmailValid ||
                  (!isDemoReference && requestWait > 0)
                }
              >
                {busy ? (
                  <>
                    <span
                      className="size-3.5 animate-spin rounded-full border-2 border-current border-t-transparent"
                      aria-hidden="true"
                    />
                    {isDemoReference ? "Opening the demo…" : "Sending your code…"}
                  </>
                ) : isDemoReference ? (
                  "Enter demo experience"
                ) : requestWait > 0 ? (
                  `Send code in ${formatOTPWait(requestWait)}`
                ) : (
                  "Email me a code"
                )}
              </button>
              {isDemoReference ? null : (
                <p className="text-xs text-(--s-muted-fg)">Codes expire after 10 minutes.</p>
              )}
            </>
          )}
        </form>
      </div>
      <div className="hidden lg:flex">
        <LoginShowcase />
      </div>
    </div>
  );
}
